import { useCallback, useEffect, useRef, useState } from "react";
import type RTKClient from "@cloudflare/realtimekit";
import {
  LIVESTREAM_VIEWER_CONFIG_MESSAGE_TYPE,
  LIVESTREAM_VIEWER_CONFIG_SYNC_REQUEST_MESSAGE_TYPE,
  buildLivestreamViewerConfigPayload,
  buildLivestreamViewerConfigSyncRequestPayload,
  parseLivestreamViewerConfig,
  parseLivestreamViewerConfigSyncRequest,
  persistRequestJoinVisible,
  persistShopVisible,
  readPersistedRequestJoinVisible,
  readPersistedShopVisible,
  type LivestreamViewerConfig,
} from "./livestreamRequestJoinVisibility";

export function useLivestreamRequestJoinVisibility({
  meeting,
  isHost,
}: {
  meeting: RTKClient | undefined;
  isHost: boolean;
}) {
  const [requestJoinVisible, setRequestJoinVisibleState] = useState(false);
  const [shopVisible, setShopVisibleState] = useState(false);
  const requestJoinVisibleRef = useRef(false);
  const shopVisibleRef = useRef(false);
  const hasLoadedPersistedRef = useRef(false);

  useEffect(() => {
    requestJoinVisibleRef.current = requestJoinVisible;
  }, [requestJoinVisible]);

  useEffect(() => {
    shopVisibleRef.current = shopVisible;
  }, [shopVisible]);

  const getViewerConfig = useCallback((): LivestreamViewerConfig => {
    return {
      requestJoinVisible: requestJoinVisibleRef.current,
      shopVisible: shopVisibleRef.current,
    };
  }, []);

  const broadcastViewerConfig = useCallback(
    async ({
      config,
      peerIds,
    }: {
      config: LivestreamViewerConfig;
      peerIds?: string[];
    }) => {
      if (!meeting?.participants) return;

      await meeting.participants.broadcastMessage(
        LIVESTREAM_VIEWER_CONFIG_MESSAGE_TYPE,
        buildLivestreamViewerConfigPayload({ config }),
        peerIds && peerIds.length > 0 ? { participantIds: peerIds } : undefined,
      );
    },
    [meeting],
  );

  const requestViewerConfigSync = useCallback(async () => {
    if (isHost || !meeting?.participants) return;

    const participantId = meeting.self.id;
    if (!participantId) return;

    await meeting.participants.broadcastMessage(
      LIVESTREAM_VIEWER_CONFIG_SYNC_REQUEST_MESSAGE_TYPE,
      buildLivestreamViewerConfigSyncRequestPayload({ participantId }),
    );
  }, [isHost, meeting]);

  useEffect(() => {
    if (!meeting?.participants) return;

    const onBroadcastedMessage = ({
      type,
      payload,
    }: {
      type: string;
      payload: Record<string, boolean | number | string | Date>;
    }) => {
      if (type === LIVESTREAM_VIEWER_CONFIG_MESSAGE_TYPE) {
        const partial = parseLivestreamViewerConfig({ payload });
        if (!partial) return;
        if (typeof partial.requestJoinVisible === "boolean") {
          setRequestJoinVisibleState(partial.requestJoinVisible);
        }
        if (typeof partial.shopVisible === "boolean") {
          setShopVisibleState(partial.shopVisible);
        }
        return;
      }

      if (!isHost || type !== LIVESTREAM_VIEWER_CONFIG_SYNC_REQUEST_MESSAGE_TYPE) return;

      const participantId = parseLivestreamViewerConfigSyncRequest({ payload });
      if (!participantId) return;

      void broadcastViewerConfig({
        config: getViewerConfig(),
        peerIds: [participantId],
      });
    };

    meeting.participants.on("broadcastedMessage", onBroadcastedMessage);
    return () => {
      meeting.participants.off("broadcastedMessage", onBroadcastedMessage);
    };
  }, [broadcastViewerConfig, getViewerConfig, isHost, meeting]);

  useEffect(() => {
    if (!isHost || !meeting?.meetingId) return;
    if (hasLoadedPersistedRef.current) return;

    hasLoadedPersistedRef.current = true;
    setRequestJoinVisibleState(
      readPersistedRequestJoinVisible({ meetingId: meeting.meetingId }),
    );
    setShopVisibleState(readPersistedShopVisible({ meetingId: meeting.meetingId }));
  }, [isHost, meeting?.meetingId]);

  const setRequestJoinVisible = useCallback(
    async ({ visible }: { visible: boolean }) => {
      if (!isHost) return;
      setRequestJoinVisibleState(visible);
      if (meeting?.meetingId) {
        persistRequestJoinVisible({ meetingId: meeting.meetingId, visible });
      }
      await broadcastViewerConfig({
        config: { ...getViewerConfig(), requestJoinVisible: visible },
      });
    },
    [broadcastViewerConfig, getViewerConfig, isHost, meeting?.meetingId],
  );

  const setShopVisible = useCallback(
    async ({ visible }: { visible: boolean }) => {
      if (!isHost) return;
      setShopVisibleState(visible);
      if (meeting?.meetingId) {
        persistShopVisible({ meetingId: meeting.meetingId, visible });
      }
      await broadcastViewerConfig({
        config: { ...getViewerConfig(), shopVisible: visible },
      });
    },
    [broadcastViewerConfig, getViewerConfig, isHost, meeting?.meetingId],
  );

  return {
    requestJoinVisible,
    setRequestJoinVisible,
    shopVisible,
    setShopVisible,
    requestViewerConfigSync,
  };
}
